import { useCallback } from 'react';

import { useRefetchOnWindowFocus } from './useRefetchOnWindowFocus';
import { CreditBalance, useGetMyCredits } from '../../services/credits.service';
import {
  CreditsBalanceDerived,
  deriveCreditsBalance,
} from '../../utils/credits/creditsBalanceMath';

/** Balance changes as flows run — 60s keeps the chip/card reasonably fresh without hammering the API. */
export const CREDITS_BALANCE_POLL_INTERVAL_MS = 60_000;

export interface UseCreditsBalanceResult {
  balance: CreditBalance | undefined;
  derived: CreditsBalanceDerived | undefined;
  loading: boolean;
  refetch: () => void;
}

/** enabled gates both the query (skip) and the refresh side-effects — pass IS_CREDITS_BALANCE_UI_ENABLED. */
export function useCreditsBalance(enabled: boolean): UseCreditsBalanceResult {
  const { data, loading, refetch } = useGetMyCredits({
    skip: !enabled,
    pollInterval: enabled ? CREDITS_BALANCE_POLL_INTERVAL_MS : undefined,
  });

  const refetchBalance = useCallback(() => {
    refetch();
  }, [refetch]);

  useRefetchOnWindowFocus(refetchBalance, enabled);

  const balance = data?.myCredits;

  return {
    balance,
    derived: balance ? deriveCreditsBalance(balance) : undefined,
    loading: enabled && loading,
    refetch: refetchBalance,
  };
}
